/**
 * Marketplace utility functions for expanding and collapsing marketplace groups
 */

import { MarketplaceNode } from '../constants/marketplace-constants';

/**
 * Recursively finds a node in the tree by value
 * @param treeData - Marketplace tree data structure
 * @param value - The value to find
 * @returns The found node or undefined
 */
const findNodeByValue = (
  treeData: MarketplaceNode[],
  value: string,
): MarketplaceNode | undefined => {
  for (const node of treeData) {
    if (node.value === value) {
      return node;
    }
    if (node.children && node.children.length > 0) {
      const found = findNodeByValue(node.children, value);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

/**
 * Recursively gets all leaf values from a node
 * @param node - The node to extract leaf values from
 * @returns Array of leaf marketplace values
 */
const getLeafValues = (node: MarketplaceNode): string[] => {
  if (!node.children || node.children.length === 0) {
    return [node.value];
  }
  return node.children.flatMap((child: MarketplaceNode) =>
    getLeafValues(child),
  );
};

/**
 * Expands marketplace groups (EU10, EU5, LATAM, MENA) to their individual marketplace values
 * Supports nested groups like EU10 > EU5
 * @param marketplaceValues - Array of marketplace values (can include groups like EU10, EU5)
 * @param treeData - Marketplace tree data structure from schema
 * @returns Array of individual marketplace values
 */
export const expandMarketplaceGroups = (
  marketplaceValues: string[],
  treeData: MarketplaceNode[],
): string[] => {
  const expanded = new Set<string>();

  marketplaceValues.forEach((value) => {
    const node = findNodeByValue(treeData, value);

    if (node?.children && node.children.length > 0) {
      // Recursively get all leaf values (individual marketplaces)
      const leafValues = getLeafValues(node);
      leafValues.forEach((leafValue) => expanded.add(leafValue));
    } else {
      expanded.add(value);
    }
  });

  return Array.from(expanded);
};
